import { Injectable } from '@angular/core';
import { AppFeatureKey, AppRoleKey, SensitiveDataTypeKey } from '@nk/data-entities-lib';
import { combineLatest, Observable, of, switchMap } from 'rxjs';
import { AppRoleService } from './app-role.service';
import { AppFeatureService } from './app-feature.service';
import { AnalysisApiClient, ApiClient, ApplicationApiClient, RequestOptions } from '@nk/api-client-lib';

abstract class ApiClientAccessBase implements ApiClient {
  protected constructor(
    private _apiClient: ApiClient,
    private _appFeatureService: AppFeatureService,
    private _appRoleService: AppRoleService,
  ) {}

  public get<T>(
    endPoint: string,
    options?: RequestOptions,
    featureKeys?: AppFeatureKey[],
    roleKeys?: AppRoleKey[],
    sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
  ): Observable<T> {
    return combineLatest([
      featureKeys ? this._appFeatureService.claimValidAppFeatureKeys(featureKeys) : of(undefined),
      roleKeys ? this._appRoleService.claimValidAppRoleKeys(roleKeys) : of(undefined),
    ]).pipe(
      switchMap(([filteredFeatureKeys, filteredRoleKeys]) => {
        return this._apiClient.get<T>(endPoint, options, filteredFeatureKeys, filteredRoleKeys, sensitiveDataTypeKeys);
      }),
    );
  }

  public post<T>(
    endPoint: string,
    body: unknown,
    options?: RequestOptions,
    featureKeys?: AppFeatureKey[],
    roleKeys?: AppRoleKey[],
    sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
  ): Observable<T> {
    return combineLatest([
      featureKeys ? this._appFeatureService.claimValidAppFeatureKeys(featureKeys) : of(undefined),
      roleKeys ? this._appRoleService.claimValidAppRoleKeys(roleKeys) : of(undefined),
    ]).pipe(
      switchMap(([filteredFeatureKeys, filteredRoleKeys]) => {
        return this._apiClient.post<T>(
          endPoint,
          body,
          options,
          filteredFeatureKeys,
          filteredRoleKeys,
          sensitiveDataTypeKeys,
        );
      }),
    );
  }

  public put<T>(
    endPoint: string,
    body: unknown,
    options?: RequestOptions,
    featureKeys?: AppFeatureKey[],
    roleKeys?: AppRoleKey[],
    sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
  ): Observable<T> {
    return combineLatest([
      featureKeys ? this._appFeatureService.claimValidAppFeatureKeys(featureKeys) : of(undefined),
      roleKeys ? this._appRoleService.claimValidAppRoleKeys(roleKeys) : of(undefined),
    ]).pipe(
      switchMap(([filteredFeatureKeys, filteredRoleKeys]) => {
        return this._apiClient.put<T>(
          endPoint,
          body,
          options,
          filteredFeatureKeys,
          filteredRoleKeys,
          sensitiveDataTypeKeys,
        );
      }),
    );
  }

  public patch<T>(
    endPoint: string,
    body: unknown,
    options?: RequestOptions,
    featureKeys?: AppFeatureKey[],
    roleKeys?: AppRoleKey[],
    sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
  ): Observable<T> {
    return combineLatest([
      featureKeys ? this._appFeatureService.claimValidAppFeatureKeys(featureKeys) : of(undefined),
      roleKeys ? this._appRoleService.claimValidAppRoleKeys(roleKeys) : of(undefined),
    ]).pipe(
      switchMap(([filteredFeatureKeys, filteredRoleKeys]) => {
        return this._apiClient.patch<T>(
          endPoint,
          body,
          options,
          filteredFeatureKeys,
          filteredRoleKeys,
          sensitiveDataTypeKeys,
        );
      }),
    );
  }

  public delete<T>(
    endPoint: string,
    options?: RequestOptions,
    featureKeys?: AppFeatureKey[],
    roleKeys?: AppRoleKey[],
    sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
  ): Observable<T> {
    return combineLatest([
      featureKeys ? this._appFeatureService.claimValidAppFeatureKeys(featureKeys) : of(undefined),
      roleKeys ? this._appRoleService.claimValidAppRoleKeys(roleKeys) : of(undefined),
    ]).pipe(
      switchMap(([filteredFeatureKeys, filteredRoleKeys]) => {
        return this._apiClient.delete<T>(
          endPoint,
          options,
          filteredFeatureKeys,
          filteredRoleKeys,
          sensitiveDataTypeKeys,
        );
      }),
    );
  }
}

@Injectable({ providedIn: 'root' })
export class AnalysisApiClientAccess extends ApiClientAccessBase {
  constructor(apiClient: AnalysisApiClient, appFeatureService: AppFeatureService, appRoleService: AppRoleService) {
    super(apiClient, appFeatureService, appRoleService);
  }
}

@Injectable({ providedIn: 'root' })
export class ApplicationApiClientAccess extends ApiClientAccessBase {
  public endPointPrefix: string;

  constructor(apiClient: ApplicationApiClient, appFeatureService: AppFeatureService, appRoleService: AppRoleService) {
    super(apiClient, appFeatureService, appRoleService);
    this.endPointPrefix = apiClient.endPointPrefix;
  }
}
